import {FC, useEffect, useState} from 'react'
import Button from 'app/components/Button'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useAuth} from 'app/modules/auth'
import {collection, doc, getDocs, updateDoc, onSnapshot, query, where} from '@firebase/firestore'
import db from 'services/firebase'
import {showSuccessToast} from 'app/components/ShowToast'
import {ToastContainer} from 'react-toastify'

type Props = {
  trustCenterURL: string
}

const validationSchema = Yup.object().shape({
  pathname: Yup.string()
    .required('Pathname is required')
    .test('starts-with-letter', 'Must start with alphabet or number', (value) => {
      if (typeof value !== 'string') return false
      return /^[A-Za-z0-9]/.test(value)
    })
    .test('only-alphanumeric-hyphen', 'Only alphanumeric and hyphen is allowed', (value) => {
      if (typeof value !== 'string') return false
      return /^[A-Za-z0-9\-]+$/.test(value)
    })
    .test('no-double-hyphens', 'Cannot contain double hyphen', (value) => {
      if (typeof value !== 'string') return false
      return !value.includes('--')
    })
    .test('no-trailing-hyphen', 'Cannot end with a hyphen', (value) => {
      if (typeof value !== 'string') return false
      return !value.endsWith('-')
    })
    .max(100, 'Maximum 100 characters'),
})

const SettingsTab: FC<Props> = ({trustCenterURL}) => {
  const {currentUser} = useAuth()
  const [allTrustCenterURLs, setAllTrustCenterURLs] = useState<Array<string>>([])
  const [loading, setLoading] = useState(false)
  const [oldTrustCenterPathname, setOldTrustCenterPathname] = useState('')

  useEffect(() => {
    const fetchAllTrustCenterURLs = async () => {
      try {
        const domainsRef = collection(db, 'trust_center_domains')

        const unsubscribe = onSnapshot(domainsRef, (snapshot) => {
          const data = snapshot.docs.map((doc) => doc.data().trustCenterURL)
          setAllTrustCenterURLs(data)
        })

        return unsubscribe
      } catch (error) {
        console.log('Error while fetching all trust center urls', error)
      }
    }

    fetchAllTrustCenterURLs()
  }, [])

  const formik = useFormik({
    initialValues: {pathname: ''},
    validationSchema,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        setLoading(true)

        const newTrustCenterURL = `${window.location.origin}/trust/${values.pathname.trim()}`

        if (allTrustCenterURLs.includes(newTrustCenterURL)) {
          formik.setFieldError('pathname', 'Trust Center URL is already in use')
          setSubmitting(false)
          setLoading(false)
          return
        } else {
          const tenantId = currentUser?.tenantId || ''
          const settingsRef = doc(db, 'tenants', tenantId, 'trust_center', 'settings')

          await updateDoc(settingsRef, {
            trustCenterURL: newTrustCenterURL,
          })

          const trustCenterDomainsRef = collection(db, 'trust_center_domains')
          const queryRef = query(
            trustCenterDomainsRef,
            where('trustCenterURL', '==', trustCenterURL)
          )
          const querySnapshot = await getDocs(queryRef)

          if (!querySnapshot.empty) {
            const docRef = doc(db, 'trust_center_domains', querySnapshot.docs[0].id)
            await updateDoc(docRef, {
              trustCenterURL: newTrustCenterURL,
            })
          }
        }

        setLoading(false)
        showSuccessToast({message: 'Trust Center URL has been updated successfully'})
      } catch (error) {
        console.error('Error while changing domain for trust center', error)
      }
    },
  })

  useEffect(() => {
    if (trustCenterURL.trim().length > 0) {
      const parsedUrl = new URL(trustCenterURL)
      const pathParts = parsedUrl.pathname.split('/')
      formik.setFieldValue('pathname', pathParts[pathParts.length - 1])
      setOldTrustCenterPathname(pathParts[pathParts.length - 1])
    }
  }, [trustCenterURL])

  return (
    <>
      <div className='card'>
        <div className='card-header pt-4'>
          <h3 className='text-dark fs-3 fw-bold m-4 ms-0'>Edit Trust Center URL</h3>
        </div>

        <div className='card-body'>
          <div className='form' onSubmit={() => formik.submitForm()}>
            <div className='row align-items-center'>
              <div className='col-md-12'>
                <div className='fv-row'>
                  <label htmlFor='pathname' className='fw-bold fs-6 mb-1 required'>
                    Trust Center URL
                  </label>

                  <div className='input-group'>
                    <span className={`input-group-text bg-light`}>
                      {window.location.origin}/trust/
                    </span>

                    <input
                      id='pathname'
                      type='text'
                      className={`form-control ${formik.errors.pathname ? 'is-invalid' : ''}`}
                      placeholder='Enter Your Trust Center Pathname'
                      autoComplete='off'
                      {...formik.getFieldProps('pathname')}
                    />
                  </div>

                  {formik.errors.pathname && (
                    <div className='fv-plugins-message-container'>
                      <div className='fv-help-block'>
                        <span role='alert'>{formik.errors.pathname}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card-footer py-4'>
          <div className='d-flex justify-content-end'>
            {loading ? (
              <Button btnType='primary' disabled>
                <span>Publishing...</span>
                <span className='spinner-border spinner-border-sm align-middle mx-2'></span>
              </Button>
            ) : (
              <Button
                btnType='primary'
                type='submit'
                onClick={() => formik.submitForm()}
                disabled={
                  oldTrustCenterPathname.trim() === formik.values.pathname.trim() || !formik.isValid
                }
              >
                Publish
              </Button>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />
    </>
  )
}

export default SettingsTab
